import { theme } from '@buggy/shared'
import type { ApexOptions } from 'apexcharts'
import { merge } from 'lodash-es'
import { useEffect, useMemo, useState } from 'react'
import { useThemeConstants } from '@/hooks/use-theme-constants'

const DEFAULT_OPTIONS: ApexOptions = {
  chart: {
    animations: { enabled: false },
  },
  grid: {
    xaxis: { lines: { show: true } },
    yaxis: { lines: { show: true } },
  },
  fill: { opacity: 1 },
  plotOptions: {
    bar: {
      dataLabels: { position: 'top' },
      columnWidth: '60%',
    },
  },
  dataLabels: {
    offsetY: -16,
    style: {
      colors: [theme.colors.text.secondary],
      fontWeight: 400,
      fontSize: '10px',
      fontFamily: 'Roboto',
    },
  },
  xaxis: {
    labels: {
      style: {
        colors: theme.colors.text.tooltip,
        fontFamily: 'Roboto',
        fontSize: '10px',
        fontWeight: 400,
      },
    },
  },
  yaxis: {
    labels: {
      style: {
        colors: theme.colors.text.tooltip,
        fontFamily: 'Roboto',
        fontSize: '10px',
        fontWeight: 400,
      },
    },
    title: {
      style: {
        color: theme.colors.text.tooltip,
        fontFamily: 'Roboto',
        fontSize: '10px',
        fontWeight: 400,
      },
    },
  },
}

type UseApexChartDataUriOptions = {
  skip: boolean
}

export const useApexChartDataUri = (
  options: ApexOptions,
  { skip }: UseApexChartDataUriOptions = { skip: false },
): string | undefined => {
  const { chartColors } = useThemeConstants()

  const chartOptions = useMemo(() => {
    if (skip) {
      return undefined
    }

    return merge({}, DEFAULT_OPTIONS, { colors: chartColors }, options)
  }, [options, chartColors, skip])

  const [dataUri, setDataUri] = useState<string>()

  useEffect(() => {
    setDataUri(undefined)

    if (!chartOptions) {
      return
    }

    const renderChart = async (): Promise<void> => {
      try {
        const ApexCharts = (await import('apexcharts')).default

        const container = document.createElement('div')

        container.style.position = 'absolute'
        container.style.left = '-9999px'

        container.style.width = chartOptions.chart?.width + 'px'

        document.body.appendChild(container)

        const chart = new ApexCharts(container, chartOptions)

        await chart.render()
        const result = await chart.dataURI({ scale: 1 })

        if ('imgURI' in result) {
          setDataUri(result.imgURI)
        }

        chart.destroy()
        document.body.removeChild(container)
      } catch (error) {
        console.error(error)
        setDataUri('')
      }
    }

    renderChart()
  }, [chartOptions])

  return dataUri
}

export default useApexChartDataUri
